import React, {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  InputHTMLAttributes,
  MouseEvent,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from "../Button/Button";
import { Appearance, SizeName } from "@mrocket/styles";
import {
  InputContainer,
  InputContent,
  InputLabel,
  InputRoot
} from "./Input.styles";

export interface InputProps extends
  Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>
{
  size?: SizeName
  appearance?: Exclude<Appearance, 'tinted' | 'default'>
  after?: ReactNode
  before?: ReactNode
  clear?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>((
  {
    className,
    size = 'medium',
    clear,
    after,
    before,
    onFocus,
    onBlur,
    onChange,
    width,
    value,
    style,
    appearance,
    ...props
  },
  ref
) => {


  const [focus, setFocus] = useState<boolean>(false)
  const changeEventRef = useRef<ChangeEvent<HTMLInputElement>>()

  const valueIsNotEmpty = useMemo(() => {
    return changeEventRef.current && value && String(value).length !== 0
  }, [value])

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setTimeout(() => setFocus(false), 0)
    onBlur && onBlur(e)
  }
  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setTimeout(() => setFocus(true), 0)
    onFocus && onFocus(e)
  }

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    changeEventRef.current = e
    onChange?.(e)
  }, [onChange])

  const handleClearValue = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (onChange && changeEventRef.current) {
      changeEventRef.current.target.value = ''
      onChange(changeEventRef.current)
    }
  }, [onChange])

  return (
    <InputContainer
      onBlur={handleBlur}
      onFocus={handleFocus}
      width={width}
      style={style}
    >
      {/*{label && <InputText size={2} appearance={valueIsNotEmpty ? 'primary' : 'secondary'}>{label}</InputText>}*/}
      <InputLabel
        data-focus={focus}
        appearance={appearance}
        size={size}
      >

        {before && <span className={'before'}>{before}</span>}
        <InputRoot
          {...props}
          inputSize={size}
          value={value}
          ref={ref}
          className={className}
          autoCorrect="off"
          autoCapitalize="off"
          autoComplete="off"
          spellCheck="false"
          onChange={handleChange}
          after={after}
        />
        <InputContent onClick={(e) => e.stopPropagation()}>
          {clear && valueIsNotEmpty && (
            <Button
              size={size !== 'tiny' ? 'small' : 'tiny'}
              onClick={handleClearValue}
              appearance={'plain'}
              icon={{name: 'xmark.circle.fill', size: 'tiny', color: '3'}}
            />
          )}
        </InputContent>
        {after && <span className={'after'}>{after}</span>}
      </InputLabel>
    </InputContainer>
  );
});

Input.displayName = 'Input'


